import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { UTILITIES } from "constants/action-types";
import siteConfig from "site-config";
import { stringify } from "query-string";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";

export const fetchPermissionTypes = (action$, store) =>
    action$.ofType(UTILITIES.USER_PERMISSION_TYPES.FETCH.START).switchMap(() => {
        return ajax(`${siteConfig.api}/nuorder/management/permissions`)
            .flatMap(({ response }) => [
                {
                    payload: response,
                    type: UTILITIES.USER_PERMISSION_TYPES.FETCH.SUCCESS,
                },
            ])
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: UTILITIES.USER_PERMISSION_TYPES.FETCH.SUCCESS,
                    })
                )
            );
    });

export const fetchNuOrderUsersEpic = (action$, store) =>
    action$.ofType(UTILITIES.USERS.FETCH.START).switchMap(() => {
        return ajax(`${siteConfig.api}/users/all/nuorder`)
            .flatMap(({ response }) => [
                {
                    payload: response,
                    type: UTILITIES.USERS.FETCH.SUCCESS,
                },
            ])
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: UTILITIES.USERS.FETCH.SUCCESS,
                    })
                )
            );
    });

export const fetchLookupUserEpic = (action$, store) =>
    action$.ofType(UTILITIES.USERS.LOOKUP.FETCH.START).switchMap(({ payload: { email } }) =>
        ajax(`${siteConfig.api}/nuorder/management/user/${email}`)
            .flatMap(({ response }) => [
                {
                    payload: response,
                    type: UTILITIES.USERS.LOOKUP.FETCH.SUCCESS,
                },
            ])
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error?.status === 404 ? formatMessage("User not found") : error,
                        type: UTILITIES.USERS.LOOKUP.FETCH.SUCCESS,
                    })
                )
            )
    );

export const fetchLookupUserCollectionEpic = (action$, store) =>
    action$
        .ofType(UTILITIES.USERS.LOOKUP.COLLECTIONS.FETCH.START)
        .switchMap(({ payload: { id, account_type, collection, limit, offset } }) =>
            ajax(
                `${siteConfig.api}/nuorder/management/${account_type}/${id}/${collection}?${stringify({ limit, offset })}`
            )
                .flatMap(({ response }) => [
                    {
                        payload: { response, collection },
                        type: UTILITIES.USERS.LOOKUP.COLLECTIONS.FETCH.SUCCESS,
                    },
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: { error, collection },
                            type: UTILITIES.USERS.LOOKUP.COLLECTIONS.FETCH.SUCCESS,
                        })
                    )
                )
        );

export const createUserEpic = (action$, store) =>
    action$
        .ofType(UTILITIES.USERS.CREATE.START)
        .switchMap(({ payload: { name, email, accountType, accountGroup, permissions } }) =>
            ajax({
                body: {
                    name,
                    email,
                    account_type: accountType,
                    account_group: accountGroup,
                    permissions,
                },
                url: `${siteConfig.api}/user/new/${accountType}`,
                method: "PUT",
                activeBrand: "create_new",
                headers: {
                    "Content-Type": "application/json",
                    Module: "management",
                    "X-Requested-With": "XMLHttpRequest",
                },
            })
                .flatMap(({ response }) => [
                    {
                        payload: response,
                        type: UTILITIES.USERS.CREATE.SUCCESS,
                    },
                    snackbarAdd({ message: formatMessage("New user created") }),
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: UTILITIES.USERS.CREATE.SUCCESS,
                        }),
                        Observable.of(snackbarAdd({ message: getCreateUserErrorMessage(error) }))
                    )
                )
        );

const getCreateUserErrorMessage = ({ status }) => {
    switch (status) {
        case 401:
            return formatMessage("You do not have permission to save user");
        case 403:
            return formatMessage("Account mismatch");
        case 409:
            return formatMessage("This email is already in use");
    }

    return formatMessage("Error saving user");
};

export const editUserEpic = (action$, store) =>
    action$
        .ofType(UTILITIES.USERS.EDIT.START)
        .switchMap(
            ({ payload: { _id, name, email, accountType, accountGroup, permissions, merchant_connections } }) => {
                const body = {
                    name,
                    email,
                    account_type: accountType,
                    account_group: accountGroup,
                } as any;

                if (permissions) {
                    // ensure we are not accidentally clearing just because a form doesn't contain it
                    body.permissions = permissions;
                }

                if (merchant_connections) {
                    // ensure we are not accidentally clearing just because a form doesn't contain it
                    body.merchant_connections = merchant_connections;
                }

                return ajax({
                    body,
                    url: `${siteConfig.api}/user/${_id}`,
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Module: "management",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                })
                    .flatMap(({ response }) => [
                        {
                            payload: response,
                            type: UTILITIES.USERS.EDIT.SUCCESS,
                        },
                        snackbarAdd({ message: formatMessage("User updated") }),
                    ])
                    .catch((error) =>
                        Observable.merge(
                            Observable.of({
                                error: true,
                                payload: error,
                                type: UTILITIES.USERS.EDIT.SUCCESS,
                            }),
                            Observable.of(snackbarAdd({ message: getCreateUserErrorMessage(error) }))
                        )
                    );
            }
        );

export const disableUserEpic = (action$, store) =>
    action$.ofType(UTILITIES.USERS.DELETE.START).switchMap(({ payload }) => {
        const { id } = payload;
        return ajax({
            url: `${siteConfig.api}/user/${id}`,
            method: "DELETE",
        })
            .flatMap(({ response }) => {
                return [
                    {
                        payload: response,
                        type: UTILITIES.USERS.DELETE.SUCCESS,
                    },
                    snackbarAdd({
                        message: formatMessage(`Total number of users disabled: ${response.disabled_emails_count}`),
                    }),
                ];
            })
            .catch((error) => {
                return Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        type: UTILITIES.USERS.DELETE.SUCCESS,
                    }),
                    Observable.of(
                        snackbarAdd({ message: formatMessage("Error deleting user, please see Jesse for help") })
                    )
                );
            });
    });

export const disableUserByEmailEpic = (action$, store) =>
    action$.ofType(UTILITIES.USERS.DISABLE.START).switchMap(({ payload }) => {
        const { email, reason } = payload;
        return ajax({
            url: `${siteConfig.api}/users/disable`,
            method: "POST",
            body: { email, reason },
        })
            .flatMap(({ response }) => {
                return [
                    {
                        payload: response,
                        type: UTILITIES.USERS.DISABLE.SUCCESS,
                    },
                    snackbarAdd({
                        message: formatMessage("User disable success: ") + response.success,
                    }),
                ];
            })
            .catch((error) => {
                let err = formatMessage("Error disabling user.");
                if (error?.xhr?.response?.message) {
                    err = "Error: " + error.xhr.response.message;
                } else if (error.status === 401) {
                    err = formatMessage("You do not have permission.");
                }
                return Observable.merge(
                    Observable.of({
                        error: true,
                        payload: err,
                        type: UTILITIES.USERS.DISABLE.SUCCESS,
                    }),
                    Observable.of(snackbarAdd({ message: formatMessage(err) }))
                );
            });
    });

export const restoreUserByEmailEpic = (action$, store) =>
    action$.ofType(UTILITIES.USERS.RESTORE.START).switchMap(({ payload }) => {
        const { email, reason } = payload;
        return ajax({
            url: `${siteConfig.api}/users/restore`,
            method: "POST",
            body: { email, reason },
        })
            .flatMap(({ response }) => {
                return [
                    {
                        payload: response,
                        type: UTILITIES.USERS.RESTORE.SUCCESS,
                    },
                    snackbarAdd({
                        message: formatMessage("User restore success: ") + response.success,
                    }),
                ];
            })
            .catch((error) => {
                let err = formatMessage("Error restoring user.");
                if (error?.xhr?.response?.message) {
                    err = "Error: " + error.xhr.response.message;
                } else if (error.status === 401) {
                    err = formatMessage("You do not have permission.");
                }
                return Observable.merge(
                    Observable.of({
                        error: true,
                        payload: err,
                        type: UTILITIES.USERS.RESTORE.SUCCESS,
                    }),
                    Observable.of(snackbarAdd({ message: formatMessage(err) }))
                );
            });
    });
